import React, { useState, useRef, useEffect } from 'react';
import { Clock, ChevronUp, ChevronDown, Keyboard } from 'lucide-react';

interface TimePickerProps {
  value: string; // HH:MM
  onChange: (time: string) => void;
  disabled?: boolean;
}

export const TimePicker: React.FC<TimePickerProps> = ({ value, onChange, disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hour, setHour] = useState<number>(7);
  const [minute, setMinute] = useState<number>(0);
  const [manualHour, setManualHour] = useState<string>('');
  const [manualMinute, setManualMinute] = useState<string>('');
  const [showManualInput, setShowManualInput] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const hourInputRef = useRef<HTMLInputElement>(null);
  const minuteInputRef = useRef<HTMLInputElement>(null);

  // Parse value inicial e arredondar minutos quando o picker abre
  useEffect(() => {
    if (value) {
      const [h, m] = value.split(':').map(Number);
      if (!isNaN(h) && !isNaN(m)) {
        // Quando o picker abre, arredondar minuto para múltiplo de 15
        let roundedMinute = m;
        if (isOpen) {
          if (m <= 7) {
            roundedMinute = 0;
          } else if (m <= 22) {
            roundedMinute = 15;
          } else if (m <= 37) {
            roundedMinute = 30;
          } else if (m <= 52) {
            roundedMinute = 45;
          } else {
            roundedMinute = 45;
          }
        }
        setHour(h);
        setMinute(roundedMinute);
      }
    }
  }, [value, isOpen]);

  // Focar input quando mostrar modo manual
  useEffect(() => {
    if (showManualInput && hourInputRef.current) {
      hourInputRef.current.focus();
      hourInputRef.current.select();
    }
  }, [showManualInput]);

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setShowManualInput(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const incrementHour = () => {
    const newHour = hour === 23 ? 0 : hour + 1;
    setHour(newHour);
    updateTime(newHour, minute);
  };

  const decrementHour = () => {
    const newHour = hour === 0 ? 23 : hour - 1;
    setHour(newHour);
    updateTime(newHour, minute);
  };

  const incrementMinute = () => {
    const newMinute = minute === 45 ? 0 : minute + 15;
    setMinute(newMinute);
    updateTime(hour, newMinute);
  };

  const decrementMinute = () => {
    const newMinute = minute === 0 ? 45 : minute - 15;
    setMinute(newMinute);
    updateTime(hour, newMinute);
  };

  const updateTime = (h: number, m: number) => {
    const formattedTime = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    onChange(formattedTime);
  };

  const handleHourChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let input = e.target.value.replace(/[^0-9]/g, '');

    // Limitar a 2 dígitos
    if (input.length > 2) {
      input = input.slice(0, 2);
    }

    // Validar hora (00-23)
    let numValue = parseInt(input) || 0;
    if (numValue > 23) {
      numValue = 23;
      input = '23';
    }

    setManualHour(input);

    // Auto-focar minuto quando digitar 2 dígitos
    if (input.length === 2 && minuteInputRef.current) {
      minuteInputRef.current.focus();
      minuteInputRef.current.select();
    }
  };

  const handleMinuteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let input = e.target.value.replace(/[^0-9]/g, '');

    // Limitar a 2 dígitos
    if (input.length > 2) {
      input = input.slice(0, 2);
    }

    // Validar minuto (00-59)
    let numValue = parseInt(input) || 0;
    if (numValue > 59) {
      numValue = 45; // Volta para o maior múltiplo de 15
      input = '45';
    }

    setManualMinute(input);
  };

  const handleManualInputSubmit = () => {
    const h = parseInt(manualHour) || 0;
    const m = parseInt(manualMinute) || 0;

    // Validar hora (00-23)
    const validH = Math.min(23, Math.max(0, h));

    // Validar minuto (00-59) - SEM arredondar
    // O usuário pode digitar qualquer valor e será aceito exatamente como digitado
    const validM = Math.min(59, Math.max(0, m));

    // Salvar valores exatos (não arredondados)
    setHour(validH);
    setMinute(validM);
    updateTime(validH, validM);
    setShowManualInput(false);
    setIsOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, field: 'hour' | 'minute') => {
    if (e.key === 'Enter') {
      if (field === 'hour' && minuteInputRef.current) {
        minuteInputRef.current.focus();
        minuteInputRef.current.select();
      } else {
        handleManualInputSubmit();
      }
    } else if (e.key === 'Escape') {
      setShowManualInput(false);
    }
  };

  const toggleManualInput = () => {
    setShowManualInput(!showManualInput);
    if (!showManualInput) {
      setManualHour(String(hour).padStart(2, '0'));
      setManualMinute(String(minute).padStart(2, '0'));
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Input Button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className="w-full px-4 py-2.5 border-2 border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-60 disabled:cursor-not-allowed text-left flex items-center justify-between hover:border-blue-400 transition-colors"
        aria-label="Selecionar horário"
      >
        <span className="text-lg tabular-nums">{value || '07:00'}</span>
        <Clock className="w-5 h-5 text-blue-500" />
      </button>

      {/* Time Picker Dropdown - Reduzido em 10% */}
      {isOpen && (
        <div className="absolute z-50 mt-2 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 rounded-xl shadow-2xl w-72">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-3 rounded-t-xl text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Clock className="w-5 h-5" />
              <span className="font-bold">Selecione o Horário</span>
            </div>
          </div>

          <div className="p-5">
            {!showManualInput ? (
              <>
                {/* Spinner-Style Time Selection */}
                <div className="flex items-center justify-center gap-3">
                  {/* Hour Spinner */}
                  <div className="flex flex-col items-center">
                    <button
                      type="button"
                      onClick={incrementHour}
                      className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900 rounded-lg transition-colors"
                      aria-label="Aumentar hora"
                    >
                      <ChevronUp className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                    </button>

                    <div className="bg-blue-50 dark:bg-blue-900/30 rounded-xl px-5 py-3 my-2">
                      <div className="text-4xl font-bold text-blue-600 dark:text-blue-400 tabular-nums min-w-[70px] text-center">
                        {String(hour).padStart(2, '0')}
                      </div>
                      <div className="text-xs text-center text-gray-500 dark:text-gray-400 mt-1 font-semibold">
                        HORA
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={decrementHour}
                      className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900 rounded-lg transition-colors"
                      aria-label="Diminuir hora"
                    >
                      <ChevronDown className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                    </button>
                  </div>

                  {/* Separator */}
                  <div className="text-4xl font-bold text-gray-400 dark:text-gray-500">:</div>

                  {/* Minute Spinner */}
                  <div className="flex flex-col items-center">
                    <button
                      type="button"
                      onClick={incrementMinute}
                      className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900 rounded-lg transition-colors"
                      aria-label="Aumentar minuto"
                    >
                      <ChevronUp className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                    </button>

                    <div className="bg-blue-50 dark:bg-blue-900/30 rounded-xl px-5 py-3 my-2">
                      <div className="text-4xl font-bold text-blue-600 dark:text-blue-400 tabular-nums min-w-[70px] text-center">
                        {String(minute).padStart(2, '0')}
                      </div>
                      <div className="text-xs text-center text-gray-500 dark:text-gray-400 mt-1 font-semibold">
                        MINUTO
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={decrementMinute}
                      className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900 rounded-lg transition-colors"
                      aria-label="Diminuir minuto"
                    >
                      <ChevronDown className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                    </button>
                  </div>
                </div>

                {/* Toggle Manual Input Button */}
                <div className="mt-5 pt-3 border-t border-gray-200 dark:border-gray-600">
                  <button
                    type="button"
                    onClick={toggleManualInput}
                    className="w-full py-2.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
                  >
                    <Keyboard className="w-4 h-4" />
                    Digitar Horário
                  </button>
                </div>
              </>
            ) : (
              <>
                {/* Manual Input Mode com : fixo */}
                <div className="space-y-4">
                  <div className="text-center text-sm text-gray-600 dark:text-gray-400">
                    Digite o horário (HH:MM)
                  </div>

                  {/* HH : MM com : fixo */}
                  <div className="flex items-center justify-center gap-2">
                    <input
                      ref={hourInputRef}
                      type="text"
                      value={manualHour}
                      onChange={handleHourChange}
                      onKeyDown={(e) => handleKeyDown(e, 'hour')}
                      placeholder="00"
                      maxLength={2}
                      className="w-20 text-center text-3xl font-bold px-2 py-2 border-2 border-blue-300 dark:border-blue-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white tabular-nums"
                    />
                    <span className="text-3xl font-bold text-gray-400 dark:text-gray-500">:</span>
                    <input
                      ref={minuteInputRef}
                      type="text"
                      value={manualMinute}
                      onChange={handleMinuteChange}
                      onKeyDown={(e) => handleKeyDown(e, 'minute')}
                      placeholder="00"
                      maxLength={2}
                      className="w-20 text-center text-3xl font-bold px-2 py-2 border-2 border-blue-300 dark:border-blue-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white tabular-nums"
                    />
                  </div>

                  <div className="text-xs text-center text-gray-500 dark:text-gray-400">
                    Hora: 00-23 | Minuto: 00-59 (aceita qualquer valor)
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setShowManualInput(false)}
                      className="py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-semibold transition-colors"
                    >
                      Voltar
                    </button>
                    <button
                      type="button"
                      onClick={handleManualInputSubmit}
                      className="py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors"
                    >
                      Confirmar
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* Confirm Button (only in spinner mode) */}
            {!showManualInput && (
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="w-full mt-3 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold transition-colors shadow-md"
              >
                Confirmar
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
